/**
 * Onboarding em três passos.
 *
 * **Uma decisão por tela** (docs/06_DESIGN_SYSTEM.md §1). Nome, papel, aceites
 * — cada passo com um botão primário e nada disputando atenção com ele.
 *
 * Poderia caber tudo numa tela só, e caberia mal: a primeira coisa que a
 * pessoa faz no produto seria encarar um formulário longo com três seções e
 * uma caixa de aviso sobre saúde. Três telas curtas levam o mesmo tempo e não
 * parecem cadastro de banco.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import {
  type ConsentDocument,
  type OnboardingPayload,
  type SelfAssignableRole,
  submitOnboarding,
} from '@/features/identity/api';
import { ME_KEY } from '@/features/identity/useMe';
import { takeRememberedInvite } from '@/features/relationships/pendingInvite';

const TOTAL_STEPS = 3;

const CONSENTS: ReadonlyArray<{ document: ConsentDocument; key: string; hint?: string }> = [
  { document: 'terms', key: 'onboarding:consent.terms' },
  { document: 'privacy', key: 'onboarding:consent.privacy' },
  {
    document: 'health_disclaimer',
    key: 'onboarding:consent.health',
    hint: 'onboarding:consent.healthHint',
  },
];

export default function OnboardingPage() {
  const { t, i18n } = useTranslation(['onboarding', 'common', 'errors']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1);
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<Set<SelfAssignableRole>>(new Set());
  const [accepted, setAccepted] = useState<Set<ConsentDocument>>(new Set());
  const [failed, setFailed] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload: OnboardingPayload) => submitOnboarding(payload),
    onSuccess: (me) => {
      queryClient.setQueryData(ME_KEY, me);
      // Quem chegou por um link de convite volta para ele. Sem isto, o
      // onboarding terminaria na tela Hoje e o convite se perderia — o token
      // vive numa URL que ninguém mais tem.
      const pending = takeRememberedInvite();
      navigate(pending ? `/convite/${pending}` : '/today', { replace: true });
    },
    onError: () => setFailed(true),
  });

  function toggle<T>(set: Set<T>, value: T): Set<T> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }

  function handleSubmit() {
    setFailed(false);
    mutation.mutate({
      display_name: displayName.trim(),
      locale: i18n.language,
      unit_system: 'metric',
      // O fuso vem do aparelho. Perguntar seria uma tela a mais para obter o
      // que o navegador já sabe — e a pessoa acertaria menos que ele.
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      jurisdiction: 'BR',
      roles: [...roles],
      consents: CONSENTS.map((item) => ({
        document: item.document,
        accepted: accepted.has(item.document),
      })),
    });
  }

  const allAccepted = CONSENTS.every((item) => accepted.has(item.document));

  return (
    <main className="mh-shell justify-center px-5 py-10">
      <Brand />
      <p className="mb-6 text-sm font-medium text-(--color-sand-500)">
        {t('onboarding:progress', { current: step, total: TOTAL_STEPS })}
      </p>

      {step === 1 ? (
        <section>
          <h1 className="text-3xl">{t('onboarding:name.title')}</h1>
          <p className="mt-1 mb-8 text-(--color-sand-700)">{t('onboarding:name.subtitle')}</p>

          <div className="mh-field">
            <label htmlFor="displayName">{t('onboarding:name.label')}</label>
            <input
              id="displayName"
              type="text"
              autoComplete="nickname"
              autoFocus
              maxLength={80}
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>

          <button
            type="button"
            className="mh-button mh-button--primary mt-6 w-full"
            disabled={displayName.trim().length === 0}
            onClick={() => setStep(2)}
          >
            {t('onboarding:name.next')}
          </button>
        </section>
      ) : null}

      {step === 2 ? (
        <section>
          <h1 className="text-3xl">{t('onboarding:role.title')}</h1>
          <p className="mt-1 mb-6 text-(--color-sand-700)">{t('onboarding:role.subtitle')}</p>

          {/* Caixas de seleção, não escolha única: aluno e profissional
              coexistem, e o profissional que também treina é caso comum. */}
          <div className="flex flex-col gap-3">
            {(['student', 'professional'] as const).map((role) => {
              const active = roles.has(role);
              return (
                <button
                  key={role}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setRoles(toggle(roles, role))}
                  className={[
                    'rounded-2xl border-2 p-4 text-left transition-colors',
                    active
                      ? 'border-(--color-amber-500) bg-(--color-amber-50)'
                      : 'border-(--color-sand-200) bg-(--color-sand-0)',
                  ].join(' ')}
                >
                  <span className="block font-semibold text-(--color-sand-900)">
                    {t(`onboarding:role.${role}`)}
                  </span>
                  <span className="mt-1 block text-sm text-(--color-sand-700)">
                    {t(`onboarding:role.${role}Hint`)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex gap-3">
            <button type="button" className="mh-button mh-button--ghost" onClick={() => setStep(1)}>
              {t('onboarding:back')}
            </button>
            <button
              type="button"
              className="mh-button mh-button--primary flex-1"
              disabled={roles.size === 0}
              onClick={() => setStep(3)}
            >
              {t('onboarding:role.next')}
            </button>
          </div>
        </section>
      ) : null}

      {step === 3 ? (
        <section>
          <h1 className="text-3xl">{t('onboarding:consent.title')}</h1>
          <p className="mt-1 mb-6 text-(--color-sand-700)">{t('onboarding:consent.subtitle')}</p>

          <div className="flex flex-col gap-4">
            {CONSENTS.map((item) => (
              <label
                key={item.document}
                className="flex cursor-pointer items-start gap-3 rounded-xl bg-(--color-sand-0) p-4"
              >
                <input
                  type="checkbox"
                  checked={accepted.has(item.document)}
                  onChange={() => setAccepted(toggle(accepted, item.document))}
                  className="mt-0.5 h-6 w-6 shrink-0 accent-(--color-amber-500)"
                />
                <span>
                  <span className="block text-sm font-medium text-(--color-sand-900)">
                    {t(item.key)}
                  </span>
                  {item.hint ? (
                    <span className="mt-1 block text-sm text-(--color-sand-700)">
                      {t(item.hint)}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
          </div>

          {failed ? (
            <div className="mt-4">
              <Alert tone="error" live="assertive">
                {t('onboarding:failed')}
              </Alert>
            </div>
          ) : null}

          <div className="mt-6 flex gap-3">
            <button type="button" className="mh-button mh-button--ghost" onClick={() => setStep(2)}>
              {t('onboarding:back')}
            </button>
            <button
              type="button"
              className="mh-button mh-button--primary flex-1"
              disabled={!allAccepted || mutation.isPending}
              onClick={handleSubmit}
            >
              {mutation.isPending ? t('onboarding:saving') : t('onboarding:consent.submit')}
            </button>
          </div>

          {!allAccepted ? (
            <p className="mt-3 text-center text-sm text-(--color-sand-700)">
              {t('onboarding:consent.required')}
            </p>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
